<ng-template
  #searchGroupTemplate
  let-searchGroup="group"
  let-i="index"
  let-isFirst="isFirst"
  let-isFilter="isFilter"
  let-formGroup="formGroup"
>
  <div class="search-group" [formGroup]="formGroup">
    <div class="join-type-symbol" [ngClass]="{ and: true }">
      @if (isFirst) {
        <div class="icon">
          <div class="circle"></div>
        </div>
      }
      @if (!isFirst) {
        <div class="text">
          {{ searchGroup.joinOption.joinType }}
        </div>
      }
    </div>
    <div class="join-group-item">
      @if (isFilter) {
        <div class="title">
          {{ searchGroup.title }}
        </div>
      }
      @if (!isFilter) {
        <div class="join-type">
          {{ searchGroup.joinOption.name }}
        </div>
      }
      <div class="search-input">
        @if (isFilter) {
          <app-dropdown-checkbox
            [options]="searchGroup.items"
            [formControlName]="searchGroup.formControlName"
            [options$]="searchGroup.options$"
            [serverFiltering]="searchGroup.serverFiltering"
            (searchChanged)="onSearchChanged($event, searchGroup)"
            [hasMore$]="searchGroup.hasMore$"
            [loadMore]="searchGroup.loadMore"
            [resolveOptionsByValue]="searchGroup.itemsValueProvider"
          ></app-dropdown-checkbox>
        }

        @if (!isFilter) {
          <sui-tags
            [options]="searchGroup.joinOption.items | tagsOptions"
            [formControlName]="searchGroup.formControlName"
          ></sui-tags>
        }
        @if (
          formGroup.get([searchGroup.formControlName])?.dirty &&
          formGroup.get([searchGroup.formControlName])?.hasError("required")
        ) {
          <div class="text-danger">
            {{ "search-builder.error.this-field-is-required" | translate }}
          </div>
        }
      </div>
      <div class="tool">
        @if (!searchGroup.required) {
          <a
            (click)="
              removeSearchGroup(
                isFilter ? 'filter' : 'term',
                searchGroup.joinOption
              )
            "
          >
            <fa-icon [size]="'lg'" [icon]="['fas', 'minus-circle']"></fa-icon>
          </a>
        }
      </div>
    </div>
  </div>
</ng-template>

<div class="search-builder-container" [formGroup]="formGroup">
  <div class="search-builder">
    <div class="builder-selector">
      @if (visibleTabs.length) {
        <sui-tabs
          [tabs]="tabItems"
          [value]="builderType"
          (valueChange)="changeBuilderType($any($event))"
        ></sui-tabs>
      }
    </div>

    <!-- ✅ Builder -->
    @if (searchBuilderEnabled && builderType == "builder") {
      <div class="search-builder-simple editor">
        <!-- Term search groups -->
        @for (
          searchGroup of termSearchGroups;
          track searchGroup;
          let i = $index;
          let isFirst = $first
        ) {
          <ng-container
            *ngTemplateOutlet="
              searchGroupTemplate;
              context: {
                group: searchGroup,
                index: i,
                isFirst: isFirst,
                isFilter: false,
                formGroup: formGroup,
              }
            "
          ></ng-container>
        }
        <!-- Filter search groups (optional use if structure matches or similar) -->
        @for (
          searchGroup of filterSearchGroups;
          track searchGroup;
          let i = $index;
          let isFirst = $first
        ) {
          <ng-container
            *ngTemplateOutlet="
              searchGroupTemplate;
              context: {
                group: searchGroup,
                index: i,
                isFirst: false,
                isFilter: true,
                formGroup: formGroup,
              }
            "
          ></ng-container>
        }
        <div class="toolbar">
          <sui-button
            variant="secondary"
            size="lg"
            [leadingIcon]="['fas', 'plus']"
            [trailingIcon]="['fas', 'caret-down']"
            [expanded]="addTermDropdownRef.isOpen()"
            style="width: 17.85rem"
            [suiPopoverTrigger]="addTermPopover"
            #addTermDropdownRef="suiPopoverTrigger"
          >
            {{ "search-builder.btn.add-term-condition" | translate }}
          </sui-button>
          <ng-template #addTermPopover>
            <div
              suiPopover
              class="dropdown-menu"
              aria-labelledby="dropdownMenuButton"
            >
              @for (
                joinOption of availableTermJoinOptions;
                track trackByValue($index, joinOption)
              ) {
                <div class="dropdown-menu-item">
                  <sui-checkbox
                    [disabled]="joinOption.required"
                    [ngModel]="isJoinOptionSelected('term', joinOption)"
                    [ngModelOptions]="{ standalone: true }"
                    (checkedChange)="
                      toggleSearchGroupSelection('term', joinOption)
                    "
                  >
                    <fa-icon [icon]="joinOption.icon"> </fa-icon>
                    <a [innerHTML]="joinOption.name"></a>
                  </sui-checkbox>
                </div>
              }
            </div>
          </ng-template>
          <sui-button
            variant="secondary"
            size="lg"
            [leadingIcon]="['fas', 'filter']"
            [trailingIcon]="['fas', 'caret-down']"
            [expanded]="addFilterDropdownRef.isOpen()"
            style="width: 17.85rem"
            [suiPopoverTrigger]="addFilterPopover"
            #addFilterDropdownRef="suiPopoverTrigger"
          >
            {{ "search-builder.btn.add-filter-condition" | translate }}
          </sui-button>
          <ng-template #addFilterPopover>
            <div suiPopover class="dropdown-menu">
              @for (
                joinOption of availableFilterJoinOptions;
                track trackByValue($index, joinOption)
              ) {
                <div class="dropdown-menu-item">
                  <sui-checkbox
                    [ngModel]="isJoinOptionSelected('filter', joinOption)"
                    [ngModelOptions]="{ standalone: true }"
                    (checkedChange)="
                      toggleSearchGroupSelection('filter', joinOption)
                    "
                  >
                    <a [innerHTML]="joinOption.name"></a>
                  </sui-checkbox>
                </div>
              }
            </div>
          </ng-template>
        </div>
      </div>
    }

    <!-- ✅ Advanced -->
    @if (builderType == "editor") {
      <div class="search-builder-advanced">
        <app-query-editor
          #queryEditor
          [formControlName]="'query_text'"
          (queryChange)="onQueryChange($event)"
          (queryBlur)="onQueryBlur()"
          [isReadOnly]="isQueryEditorReadOnly"
          [ctrlEnterExtension]="ctrlEnterExtension"
          [objectTypeId]="feed?.object_type_id ?? objectTypeId"
        ></app-query-editor>
        @if (
          formGroup.get(["query_text"])?.dirty &&
          formGroup.get(["query_text"])?.hasError("required")
        ) {
          <div class="text-danger">
            {{
              "search-builder.error.please-enter-a-valid-query-string"
                | translate
            }}
          </div>
        }
      </div>
    }

    <!-- ✅ Combined -->
    @if (builderType === "combined") {
      <div class="search-builder-combined">
        <app-dataobject-list
          [selectedObjectIds]="combinedFeedIds"
          [showSelectedOnly]="true"
          (componentDataChange)="onCombinedFeedSelectionChange($event)"
          [showSelectedOnly]="!!this.feed"
          [showSearch]="true"
          [objectTypeIds]="[
            NomObjectTypes.ONLINE_FEED,
            NomObjectTypes.STORY_FEED,
            NomObjectTypes.CLIPPING_FEED,
            NomObjectTypes.SOCIAL_FEED,
            NomObjectTypes.COMBINED_FEED,
            NomObjectTypes.BROADCAST_FEED,
          ]"
        >
        </app-dataobject-list>
      </div>
    }

    <!-- ✅ History -->
    @if (builderType === "history") {
      <app-query-history-viewer
        [historyRecords]="historyRecords$ | async"
        (onUpdateHistory)="historyRecordClick($event)"
      ></app-query-history-viewer>
    }
    <!-- ✅ Social Networks -->
    @if (
      objectTypeId === NomObjectTypes.SOCIAL_FEED &&
      builderType !== "history" &&
      builderType !== "excluded-posts"
    ) {
      <div class="social-networks" id="post_social_source_id">
        @for (platform of contentPlatforms.data() ?? []; track platform.id) {
          <div>
            <sui-checkbox
              [ngModel]="
                $safeNavigationMigration(
                  formGroup
                    .get(['post_social_source_id'])
                    ?.value?.includes(platform.id)
                )
              "
              [ngModelOptions]="{ standalone: true }"
              (checkedChange)="onSocialSourceChange(platform.id, $event)"
            >
              @if (socialLogoPath("small", platform.icon.small); as logo) {
                <img [src]="logo" [alt]="platform.name" />
              }
              {{ platform.name }}
            </sui-checkbox>
          </div>
        }
        @if (
          formGroup.get(["post_social_source_id"])?.dirty &&
          formGroup.get(["post_social_source_id"])?.hasError("required")
        ) {
          <div class="text-danger">
            {{
              "search-builder.error.select-at-least-one-social-network"
                | translate
            }}
          </div>
        }
      </div>
    }

    <!-- ✅ Excluded Posts -->
    @if (builderType === "excluded-posts") {
      <div class="builder-excluded-posts">
        <app-removed-items></app-removed-items>
      </div>
    }
  </div>

  <!-- ✅ Preview Pane -->
  @if (showPreviewPane) {
    <div class="search-preview">
      <div class="preview-header" [attr.data-status]="getPreviewStatus()">
        <div class="preview-status">
          @switch (getPreviewStatus()) {
            @case ("loading") {
              <fa-icon [icon]="['fas', 'spinner']" animation="spin"></fa-icon>
              <span>{{
                "search-builder.preview.status.searching" | translate
              }}</span>
            }
            @case ("results") {
              <fa-icon [icon]="['fas', 'check-circle']"></fa-icon>
              <span class="bold">{{
                "search-builder.preview.number-of-posts-found"
                  | translate: { count: postsPreviewCount | number }
              }}</span>
            }
            @case ("empty") {
              <fa-icon [icon]="['fas', 'exclamation-circle']"></fa-icon>
              <span>{{
                "search-builder.preview.status.no-results" | translate
              }}</span>
            }
            @default {
              <fa-icon [icon]="['fas', 'magnifying-glass']"></fa-icon>
              <span>{{
                "search-builder.preview.status.edit-query-to-preview"
                  | translate
              }}</span>
            }
          }
        </div>
        <div class="preview-actions">
          @if (builderType === "editor") {
            <sui-button
              variant="secondary"
              size="lg"
              [leadingIcon]="['fas', 'question']"
              [iconOnly]="true"
              [ariaLabel]="'search-builder.button.show-hints' | translate"
              [suiTooltip]="'search-builder.button.show-hints' | translate"
              (click)="openHints()"
            ></sui-button>
          }
          <sui-button
            variant="secondary"
            size="lg"
            [leadingIcon]="['fas', 'rotate']"
            [iconOnly]="true"
            [ariaLabel]="'search-builder.button.refresh-preview' | translate"
            [suiTooltip]="'search-builder.button.refresh-preview' | translate"
            [disabled]="postsLoading"
            (click)="previewQuery()"
          ></sui-button>
        </div>
      </div>
      <div class="preview-content">
        <div class="popup-pane-preview">
          <div class="card-container posts">
            @if (postsLoading) {
              <div class="fa-center">
                <app-loading></app-loading>
              </div>
            }
            @for (post of posts; track post) {
              <app-post-card
                [post]="post"
                [disableTools]="true"
                [selectable]="false"
                [viewConfig]="previewViewConfig"
              ></app-post-card>
            }
            @if (!postLoaded) {
              <div class="app-loading-wrapper">
                <app-loading-card
                  type="post"
                  [count]="2"
                  [animationEnabled]="false"
                ></app-loading-card>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }
</div>

<!-- ✅ Query Hints Popup -->
@if (
  (queryHintsOverlayService.currentlyOpen$ | async)?.componentId ===
    componentId ||
  (queryHintsOverlayService.currentlyOpen$ | async)?.componentId ===
    "search-builder-wrapper"
) {
  <app-feed-query-hints-popup
    [objectTypeId]="feed?.object_type_id ?? objectTypeId"
    (close)="closeHints()"
    (hintSelected)="addHint($event)"
  ></app-feed-query-hints-popup>
}
